/**
 * Reports — the CFO screen (B5, ADR-053).
 *
 * The headline is the HONEST NGR: GGR minus the ledger-derived bonus cost,
 * the modelled rail fees, the audited cost drawer and affiliate earnings —
 * computed once, server-side, per currency, never cross-summed (ADR-020/053)
 * and with house bots excluded everywhere (ADR-060). This screen renders the
 * breakdown and can write exactly one thing: a manual cost entry into the
 * audited drawer. Everything else is a read.
 */
import { useState, type ReactNode } from 'react';
import {
  addCostEntry,
  COST_KINDS,
  fetchCostEntries,
  fetchExposure,
  fetchNgr,
  fetchWithdrawalSla,
  type CostKind,
  type NgrCurrencyLine,
} from '../api/reports.ts';
import type { Currency } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute } from '../lib/datetime.ts';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { Card, EmptyState, ErrorState, Money, Stat, TableSkeleton } from '../ui/primitives.tsx';
import { IconOutbound, IconPeople, IconTrend, IconVault } from '../ui/icons.tsx';

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;

export function Reports(): ReactNode {
  const [days, setDays] = useState<number>(30);
  const ngr = useAsync(() => fetchNgr(days), [days]);
  const exposure = useAsync(() => fetchExposure(), []);
  const sla = useAsync(() => fetchWithdrawalSla(), []);
  const costs = useAsync(() => fetchCostEntries(50), []);

  if (ngr.error) {
    return <ErrorState title="Could not load the NGR report" detail={ngr.error} onRetry={ngr.reload} />;
  }

  return (
    <div className="stack">
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Reporting range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`segment${days === r.days ? ' is-active' : ''}`}
              aria-pressed={days === r.days}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <span className="card__spacer" />
        {ngr.data ? (
          <span className="field__hint num">
            {ngr.data.from} → {ngr.data.to} · UTC days · bots excluded
          </span>
        ) : null}
      </div>

      <div className="stats">
        <Stat
          icon={<IconPeople size="sm" />}
          label="First-time depositors"
          value={ngr.data ? ngr.data.ftdCount.toLocaleString() : '—'}
          note="first credited deposit in range"
        />
        <Stat
          icon={<IconOutbound size="sm" />}
          label="Payout queue"
          value={sla.data ? sla.data.pendingCount.toLocaleString() : '—'}
          note={
            sla.data?.oldestHours != null
              ? `oldest ${sla.data.oldestHours} h · p95 ${sla.data.p95Hours ?? '—'} h`
              : 'nothing waiting'
          }
          tone={sla.data && sla.data.pendingCount > 0 ? 'pending' : undefined}
        />
        <Stat
          icon={<IconTrend size="sm" />}
          label="Bonus ratio"
          value={firstRatio(ngr.data?.currencies) ?? '—'}
          note="promo cost as % of GGR — the generosity guardrail"
        />
      </div>

      <Card
        title="Net gaming revenue"
        icon={<IconTrend />}
        sub="GGR − ledger-derived bonus cost − modelled rail fees − cost drawer − affiliate · per currency, never cross-summed"
      >
        {ngr.loading ? (
          <TableSkeleton columns={['text', 'num', 'num', 'num']} rows={3} />
        ) : !ngr.data || ngr.data.currencies.length === 0 ? (
          <EmptyState
            compact
            icon={<IconTrend size="lg" />}
            title="Nothing in this range"
            hint="No settled bets, promo ledger rows, credited deposits or cost entries in the window."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Currency</th>
                <th className="num">Turnover</th>
                <th className="num">GGR</th>
                <th className="num">Bonus cost</th>
                <th className="num">Rakeback</th>
                <th className="num">Affiliate</th>
                <th className="num" title="Modelled from each method's fee_pct / fee_fixed — never charged to a player">
                  Rail fees (modelled)
                </th>
                <th className="num">Cost drawer</th>
                <th className="num">NGR</th>
                <th className="num">Actives</th>
                <th className="num">ARPPU</th>
              </tr>
            </thead>
            <tbody>
              {ngr.data.currencies.map((line) => (
                <tr key={line.currency}>
                  <td>{line.currency}</td>
                  <td className="num"><Money value={line.turnover} /></td>
                  <td className="num"><Money value={line.grossGamingRevenue} colour /></td>
                  <td className="num"><Money value={line.bonusCost} /></td>
                  <td className="num"><Money value={line.rakebackCost} /></td>
                  <td className="num"><Money value={line.affiliateCost} /></td>
                  <td className="num"><Money value={line.railFeesModelled} /></td>
                  <td className="num"><Money value={line.costEntries} /></td>
                  <td className="num"><Money value={line.netGamingRevenue} colour /></td>
                  <td className="num">{line.activePlayers.toLocaleString()}</td>
                  <td className="num">{line.arppu === null ? '—' : <Money value={line.arppu} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="Liability exposure"
        icon={<IconVault />}
        sub="What the platform owes if everyone walked — player balances plus outstanding bonus encumbrance"
      >
        {exposure.loading ? (
          <TableSkeleton columns={['text', 'num', 'num', 'num']} rows={2} />
        ) : !exposure.data || exposure.data.length === 0 ? (
          <p className="field__hint">No player balances yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Currency</th>
                <th className="num">Player balances</th>
                <th className="num">Bonus locked</th>
                <th className="num" title="Rakeback earned but not yet claimed — owed the moment the player claims">
                  Rakeback unclaimed
                </th>
                <th className="num">Total exposure</th>
              </tr>
            </thead>
            <tbody>
              {exposure.data.map((line) => (
                <tr key={line.currency}>
                  <td>{line.currency}</td>
                  <td className="num"><Money value={line.playerBalances} /></td>
                  <td className="num"><Money value={line.bonusLocked} /></td>
                  <td className="num"><Money value={line.rakebackUnclaimed} /></td>
                  <td className="num"><Money value={line.total} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="Cost drawer"
        icon={<IconOutbound />}
        sub="Manual audited one-off costs — chargebacks, write-offs, true-ups. The NGR subtracts every row here"
      >
        <AddCost onAdded={() => { costs.reload(); ngr.reload(); }} />
        {costs.loading ? (
          <TableSkeleton columns={['text', 'num', 'num', 'num']} rows={3} />
        ) : !costs.data || costs.data.length === 0 ? (
          <p className="field__hint">No cost entries recorded.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Kind</th>
                <th>Currency</th>
                <th className="num">Amount</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {costs.data.map((c) => (
                <tr key={c.id}>
                  <td className="num">{absolute(c.createdAt)}</td>
                  <td>{c.kind}</td>
                  <td>{c.currency}</td>
                  <td className="num"><Money value={c.amount} /></td>
                  <td>{c.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/** The first currency's bonus ratio, for the tile; the table has the rest. */
function firstRatio(lines: NgrCurrencyLine[] | undefined): string | null {
  const withRatio = lines?.find((l) => l.bonusRatioPct !== null);
  return withRatio?.bonusRatioPct != null ? `${withRatio.bonusRatioPct}% ${withRatio.currency}` : null;
}

function AddCost({ onAdded }: { onAdded: () => void }): ReactNode {
  const [kind, setKind] = useState<CostKind>('chargeback');
  const [currency, setCurrency] = useState<Currency>('INR');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const amountValid = /^\d+(\.\d{1,4})?$/.test(amount) && Number(amount) > 0;
  const ready = amountValid && note.trim() !== '';

  async function onSubmit(): Promise<void> {
    if (!ready || busy) return;
    const result = await confirm({
      title: 'Record a cost',
      body: 'This is OUR cost, not player money — no balance changes. The entry is audited and the NGR report subtracts it from the day it is recorded.',
      confirmLabel: 'Record cost',
      facts: [
        { label: 'Kind', value: kind },
        { label: 'Amount', value: `${amount} ${currency}` },
        { label: 'Note', value: note.trim() },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await addCostEntry({ kind, currency, amount, note: note.trim() });
      toast('Cost recorded.');
      setAmount('');
      setNote('');
      onAdded();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not record the cost.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row-actions row-actions--start">
      <select className="input input--sm" value={kind} onChange={(e) => setKind(e.target.value as CostKind)}>
        {COST_KINDS.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>
      <select className="input input--sm" value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
        {(['INR', 'USDT', 'USD', 'EUR', 'BRL'] as Currency[]).map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <input
        className="input input--sm num"
        placeholder="Amount"
        inputMode="decimal"
        value={amount}
        onChange={(e) => setAmount(e.target.value.trim())}
      />
      <input
        className="input input--sm"
        placeholder="Note — what this cost is"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="button" className="btn btn--sm btn--primary" disabled={busy || !ready} onClick={() => void onSubmit()}>
        {busy ? 'Recording…' : 'Record cost'}
      </button>
    </div>
  );
}
